import { Check } from "@phosphor-icons/react";
import { Text } from "@radix-ui/themes";
import cx from "classnames";

import { interactiveSurfaceClasses } from "../../../components/InteractiveSurface";
import {
  agentComponentIdAttr,
  CHAT_ATOM_AGENT_COMPONENT_IDS,
} from "../agentComponents/chatAtomRegistry";

export type ChoiceRowProps = {
  label: string;
  description?: string;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  /** Default `question-option` — product and e2e already pin this string. */
  "data-testid"?: string;
};

/**
 * Selectable option row using the shared Carrie interactive surface.
 * why: keep the existing option layout (label + description, check when selected) — not Figma's title/meta split.
 */
export function ChoiceRow({
  label,
  description,
  selected = false,
  disabled = false,
  onClick,
  "data-testid": dataTestId = "question-option",
}: ChoiceRowProps) {
  return (
    <button
      {...agentComponentIdAttr(CHAT_ATOM_AGENT_COMPONENT_IDS.choiceRow)}
      data-testid={dataTestId}
      type="button"
      disabled={disabled}
      aria-pressed={selected}
      onClick={onClick}
      className={cx(
        "flex w-full items-center gap-1.5 px-3 py-2 text-left",
        interactiveSurfaceClasses({
          selectable: true,
          selected,
          disabled,
        }),
      )}
      title={description}
    >
      {selected ? (
        <Check
          size={14}
          weight="bold"
          className="shrink-0 text-puntt-accent-9"
          aria-hidden="true"
        />
      ) : null}
      <div className="min-w-0 flex-1">
        <Text
          as="p"
          size="2"
          weight={selected ? "medium" : "regular"}
          className={cx(
            "truncate",
            selected ? "text-puntt-accent-11" : "text-puntt-neutral-gray-12",
          )}
        >
          {label}
        </Text>
        {description ? (
          <Text
            as="p"
            size="1"
            className="mt-0.5 truncate text-puntt-neutral-gray-9"
          >
            {description}
          </Text>
        ) : null}
      </div>
    </button>
  );
}
